// The medicine / medical-service lookup behind a care-plan order form.
//
// Extracted from CarePlanActivityModal so the VOICE review screen runs the SAME
// engine instead of a second copy. Both screens order the same drugs against the
// same catalogue and the same pharmacy store, so a brand that shows OUT OF STOCK
// when typed must show OUT OF STOCK when dictated, and a charge-master selection
// must produce the same __billing_charge either way — which is what lets a
// dictated order be billed at all.
//
// Owns four pieces of state keyed by "searchKey" (the dotted field path, so one
// form can hold many independent lookups):
//   chargeOptionsByKey  - dropdown options (search_medicines/ or
//                         search_charge_master/, or substitutes)
//   chargeLoadingByKey  - per-field spinner
//   medStockByKey       - stock verdict for a picked drug
//   openOptionsKey      - which dropdown is open
//
// Two refs stay private: the debounce timers, and the "check this once a store
// is picked" replay list.

import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

// [BILLING] Debounce delay (ms) before search_charge_master/ is called after typing.
export const CHARGE_SEARCH_DEBOUNCE_MS = 1000;

// [PHARMACY_RX] search_medicines/ terms carry dose + form ("Calpol 1 g oral tablet"),
// which the inventory catalogue doesn't index — strip from the first digit onward so
// the stock lookup searches on the brand/molecule name alone ("Calpol").
export const stripDoseForm = (name) => String(name || "").replace(/\d.*$/, "").trim() || String(name || "").trim();

// [PHARMACY_RX] search_medicines/ writes the composition with the strength in
// brackets ("Paracetamol (500mg)"); the inventory catalogue spells the same
// thing flat ("Paracetamol 500mg"). Drop the brackets so the substitute lookup
// searches on a term the catalogue can actually match, keeping the strength so
// only the same composition comes back.
export const normalizeGenericTerm = (raw) =>
  String(raw || "")
    .replace(/[()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();


// [PHARMACY_RX] prescribe/search does NOT return a has_stock flag — availability
// has to be read off the quantity fields ("total_available_quantity": "0",
// batch_count 0, batches []). Reading a missing has_stock as "in stock" is what
// kept the Out of Stock badge and Substitute button from ever appearing.
// Quantities arrive as strings, and null means "unknown" (not zero), so only a
// value we can actually parse decides the answer; null propagates as unknown.
const resolveItemHasStock = (item) => {
  if (typeof item?.has_stock === "boolean") return item.has_stock;
  const rawQty =
    item?.total_available_quantity ?? item?.available_quantity ?? item?.stock;
  if (rawQty !== null && rawQty !== undefined && rawQty !== "") {
    const qty = Number(rawQty);
    if (Number.isFinite(qty)) return qty > 0;
  }
  if (typeof item?.can_fulfill === "boolean") return item.can_fulfill;
  if (Array.isArray(item?.batches)) return item.batches.length > 0;
  return null;
};

// [PHARMACY_RX] GET inventory/pharmacy/prescribe/search/ — pass q for a brand/generic/
// name lookup, or genericName instead to list in-stock substitutes for an OOS brand.
// Normalizes to { name, item_id, generic_name, has_stock } so the Autocomplete's
// item.name lookup keeps working regardless of which param drove the search.
// `locationId` is the selected pharmacy store (usePharmacyStore) — stock is per
// store, so without it the answer isn't about any real counter.
export async function fetchPharmacyMedicationOptions(getApiHeaders, { q, genericName, locationId } = {}) {
  const params = { limit: 20 };
  if (q) params.q = q;
  if (genericName) {
    params.generic_name = genericName;
    params.available_only = true;
  }
  if (locationId) params.location_id = locationId;
  const response = await axios.get(
    `${process.env.REACT_APP_BACKEND_ROUTE_URL}inventory/pharmacy/prescribe/search/`,
    { headers: getApiHeaders(), params }
  );
  const payload = response?.data?.data ?? response?.data ?? [];
  const rows = Array.isArray(payload) ? payload : (payload.results || payload.items || payload.data || []);
  return rows.map((item) => {
    const id = item?.item_id ?? item?.id ?? item?.inventory_item_id ?? "";
    const name = item?.item_name || item?.name || item?.brand_name || item?.generic_name || "";
    if (!name) return null;
    const generic = String(item?.generic_name || "").trim();
    // Brand with the composition in brackets — "Crocin 500 Advance (Paracetamol
    // 500mg)" — the same shape search_medicines/ terms already use, so a doctor
    // reads a substitute the same way as any other drug name. Skipped when the
    // brand already spells the composition out, to avoid a doubled-up label.
    const showGeneric = generic && !name.toLowerCase().includes(generic.toLowerCase());
    return {
      name,
      label: showGeneric ? `${name} (${generic})` : name,
      item_id: id,
      medical_service_id: id,
      generic_name: generic,
      has_stock: resolveItemHasStock(item),
      _raw: item,
    };
  }).filter(Boolean);
}

// [BILLING] Maps activity + field name → charge type for search_charge_master/.
// Change here if Medication/Test should search on different fields or types.
export const getChargeSearchConfig = (field, activityName, pathPrefix = []) => {
  const fieldName = String(field?.field || "").toLowerCase();

  // Medication drug name — works for both the standalone "medication" activity and
  // the "orders" modal where drug_name is nested inside medication_order.medication
  const isMedDrugField = fieldName === "name" || fieldName === "drug_name";
  const isMedContext =
    activityName === "medication" ||
    (activityName === "orders" && pathPrefix.includes("medication_order"));
  if (isMedDrugField && isMedContext) return { type: "MEDICATION" };

  if (activityName === "test" && (fieldName === "name" || fieldName === "orderdetail" || fieldName === "order_detail")) {
    return { type: "TEST" };
  }
  return null;
};

/**
 * @param {object} args
 * @param {() => object} args.getApiHeaders auth headers for the lookups.
 * @param {string|number} args.pharmacyStoreId selected store. Stock is per store,
 *   so with none picked the badge is left OFF rather than shown wrong.
 */
export default function useCarePlanServiceSearch({ getApiHeaders, pharmacyStoreId }) {
  const [chargeOptionsByKey, setChargeOptionsByKey] = useState({});
  const [chargeLoadingByKey, setChargeLoadingByKey] = useState({});
  const [medStockByKey, setMedStockByKey] = useState({});
  const [openOptionsKey, setOpenOptionsKey] = useState(null);
  const chargeSearchTimersRef = useRef({});
  const pendingStockChecksRef = useRef({});

  // [BILLING] Clear pending debounce timers on unmount.
  useEffect(() => {
    const timers = chargeSearchTimersRef.current;
    return () => {
      Object.values(timers).forEach((timerId) => clearTimeout(timerId));
    };
  }, []);

  const fetchChargeMasterOptions = useCallback(
    async (searchKey, name, chargeType) => {
      const trimmedName = String(name || "").trim();
      if (!trimmedName) {
        setChargeOptionsByKey((prev) => ({ ...prev, [searchKey]: [] }));
        return;
      }

      setChargeLoadingByKey((prev) => ({ ...prev, [searchKey]: true }));
      try {
        // [PHARMACY_RX] Medication drug-name typeahead uses search_medicines/ (SNOMED term
        // search, matches brand + generic synonyms) instead of search_charge_master/ — it has
        // no service_code, so items from this branch never populate __billing_charge (see
        // setBillingChargeSelection). Stock/OOS is checked separately once a name is picked
        // (checkMedicationStock), via inventory/pharmacy/prescribe/search/.
        let items;
        if (chargeType === "MEDICATION") {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_ROUTE_URL}search_medicines/`,
            {
              headers: getApiHeaders(),
              params: { name: trimmedName },
            }
          );
          const data = Array.isArray(response?.data?.data) ? response.data.data : [];
          items = data.slice(0, 20).map((med) => ({
            name: med?.term || "",
            id: med?.conceptId ?? "",
            // [PHARMACY_RX] Composition of the picked medicine ("Paracetamol (500mg)").
            // This is what the Substitute lookup searches on — see checkMedicationStock.
            generic_name: med?.generic_name || "",
            _raw: med,
          })).filter((med) => med.name);
        } else {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_ROUTE_URL}search_charge_master/`,
            {
              headers: getApiHeaders(),
              params: {
                name: trimmedName,
                type: chargeType,
                limit: 20,
              },
            }
          );
          items = Array.isArray(response?.data?.items) ? response.data.items : [];
        }
        setChargeOptionsByKey((prev) => ({ ...prev, [searchKey]: items }));
      } catch (error) {
        console.error("Error searching charge master:", error);
        setChargeOptionsByKey((prev) => ({ ...prev, [searchKey]: [] }));
      } finally {
        setChargeLoadingByKey((prev) => ({ ...prev, [searchKey]: false }));
      }
    },
    [getApiHeaders]
  );

  // [BILLING] Debounces fetchChargeMasterOptions — change CHARGE_SEARCH_DEBOUNCE_MS to adjust delay.
  const scheduleChargeMasterSearch = useCallback(
    (searchKey, name, chargeType) => {
      if (chargeSearchTimersRef.current[searchKey]) {
        clearTimeout(chargeSearchTimersRef.current[searchKey]);
      }
      chargeSearchTimersRef.current[searchKey] = setTimeout(() => {
        fetchChargeMasterOptions(searchKey, name, chargeType);
      }, CHARGE_SEARCH_DEBOUNCE_MS);
    },
    [fetchChargeMasterOptions]
  );

  // [PHARMACY_RX] search_medicines/ (the typeahead) doesn't carry stock, so once a drug
  // name is picked, look it up against inventory/pharmacy/prescribe/search/ (q=name) to
  // decide whether to show the red "Out of Stock" badge + Substitute button.
  const checkMedicationStock = useCallback(
    async (searchKey, drugName, selectedGenericName = "") => {
      const trimmed = String(drugName || "").trim();
      if (!trimmed) {
        delete pendingStockChecksRef.current[searchKey];
        setMedStockByKey((prev) => ({ ...prev, [searchKey]: null }));
        return;
      }
      // [PHARMACY_RX] Remembered so picking the pharmacy store AFTER the drug
      // name — the normal order when the hospital has several stores — can
      // replay this check instead of leaving the badge permanently blank.
      pendingStockChecksRef.current[searchKey] = {
        drugName: trimmed,
        genericName: selectedGenericName,
      };
      // No store picked yet → the stock answer would belong to no counter, so
      // leave the badge off entirely rather than showing a wrong one.
      if (!pharmacyStoreId) {
        setMedStockByKey((prev) => ({ ...prev, [searchKey]: null }));
        return;
      }
      setMedStockByKey((prev) => ({ ...prev, [searchKey]: { checking: true } }));
      const brandQuery = stripDoseForm(trimmed);
      // [PHARMACY_RX] The composition to substitute on is the one carried by the
      // medicine the doctor actually picked ("Paracetamol (500mg)" → "Paracetamol
      // 500mg"). Searching substitutes by brand instead returns other strengths of
      // the same brand ("Calpol" → Calpol 1000mg), which is not the same
      // composition — so the picked generic wins over anything derived from the name.
      const pickedGeneric = normalizeGenericTerm(selectedGenericName);
      try {
        const items = await fetchPharmacyMedicationOptions(getApiHeaders, {
          q: brandQuery,
          locationId: pharmacyStoreId,
        });
        const match = items.find((it) => it.name.toLowerCase() === trimmed.toLowerCase())
          || items.find((it) => it.name.toLowerCase().startsWith(brandQuery.toLowerCase()))
          || items[0]
          || null;
        setMedStockByKey((prev) => ({
          ...prev,
          [searchKey]: {
            checked: true,
            // [PHARMACY_RX] Kept so buildMedicinesFromEntries can recover the
            // generic for this drug — the queued entry itself only carries the
            // typed/selected brand name, never a generic.
            drugName: trimmed,
            hasStock: match ? match.has_stock !== false : false,
            // Picked composition first; then the catalogue's own generic for the
            // matched item; the brand name is only a last resort so the Substitute
            // button always has something to search on.
            genericName: pickedGeneric || match?.generic_name || brandQuery,
          },
        }));
      } catch {
        setMedStockByKey((prev) => ({ ...prev, [searchKey]: null }));
      }
    },
    [getApiHeaders, pharmacyStoreId]
  );

  // [PHARMACY_RX] Replaces a searchKey's options with same-composition substitutes,
  // triggered from the "Substitute" button once checkMedicationStock finds the picked
  // brand out of stock. Everything the catalogue returns for the generic is kept —
  // dropping the out-of-stock ones left the doctor staring at an empty dropdown when
  // the whole composition is short — but in-stock alternatives sort to the top, and
  // picking any of them re-runs the stock check anyway.
  const fetchSubstituteOptions = useCallback(
    async (searchKey, genericName) => {
      if (!genericName) return;
      if (!pharmacyStoreId) {
        toast.info("Select a pharmacy store first.");
        return;
      }
      setChargeLoadingByKey((prev) => ({ ...prev, [searchKey]: true }));
      try {
        const items = await fetchPharmacyMedicationOptions(getApiHeaders, {
          genericName,
          locationId: pharmacyStoreId,
        });
        const ranked = [...items].sort(
          (a, b) => Number(b.has_stock !== false) - Number(a.has_stock !== false),
        );
        setChargeOptionsByKey((prev) => ({ ...prev, [searchKey]: ranked }));
        if (ranked.length === 0) {
          toast.info(`No substitutes found for ${genericName}.`);
          return;
        }
        // The list is useless if the dropdown stays shut — open it on the field
        // the doctor just clicked Substitute on.
        setOpenOptionsKey(searchKey);
      } catch (error) {
        console.error("Error searching medicine substitutes:", error);
        toast.error("Could not load substitutes. Please try again.");
      } finally {
        setChargeLoadingByKey((prev) => ({ ...prev, [searchKey]: false }));
      }
    },
    [getApiHeaders, pharmacyStoreId]
  );

  // [PHARMACY_RX] Typing over a picked drug invalidates both the badge and the
  // replay entry — otherwise choosing a store later would re-check a name that is
  // no longer in the box. A method so pendingStockChecksRef stays private here.
  const clearMedicationStock = useCallback((searchKey) => {
    delete pendingStockChecksRef.current[searchKey];
    setMedStockByKey((prev) => ({ ...prev, [searchKey]: null }));
  }, []);

  // [PHARMACY_RX] With several pharmacy stores the doctor usually types the drug
  // name first and picks the store second — that first check was skipped for
  // want of a location_id, so replay it once a store exists. Without this the
  // Out of Stock badge never appears until the drug name is re-picked.
  useEffect(() => {
    if (!pharmacyStoreId) return;
    Object.entries(pendingStockChecksRef.current).forEach(
      ([searchKey, { drugName, genericName }]) => {
        checkMedicationStock(searchKey, drugName, genericName);
      }
    );
  }, [pharmacyStoreId, checkMedicationStock]);

  return {
    chargeOptionsByKey,
    chargeLoadingByKey,
    medStockByKey,
    openOptionsKey,
    setOpenOptionsKey,
    scheduleChargeMasterSearch,
    fetchSubstituteOptions,
    checkMedicationStock,
    clearMedicationStock,
    // Raw setters, for the two callers that fill the option cache themselves
    // rather than through scheduleChargeMasterSearch: the Investigation tab's
    // per-row dynamic-select (it queries searchChargeCaptureServices directly),
    // and the modal's open/close reset.
    setChargeOptionsByKey,
    setChargeLoadingByKey,
  };
}
